import React from 'react';
import { KitButton } from '@boystownorg/bi-cms-component-lib';
import { useTheme } from '@emotion/react';

// local imports
import { ButtonStyle, Link } from 'types/AgilityTypes';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';
import { themeColor } from 'services/styleUtils/colorUtils';

const CommonButton = ({
  text,
  link,
  buttonStyle,
  style,
  onClick,
  dataTestId,
  size = 'sm',
  endIcon,
}: {
  text: string;
  link?: Link;
  buttonStyle?: ButtonStyle;
  style?: React.CSSProperties;
  onClick?: () => void;
  dataTestId?: string;
  size?: 'sm' | 'md' | 'lg';
  endIcon?: React.ReactNode;
}) => {
  const theme = useTheme();

  const componentStyle: React.CSSProperties = {};
  if (buttonStyle) {
    componentStyle.border =
      buttonStyle.fields?.variant === 'outlined'
        ? `1px solid ${buttonColor(theme, buttonStyle.fields?.color)}`
        : 'none';
    componentStyle.color =
      buttonStyle.fields?.variant === 'outlined'
        ? buttonColor(theme, buttonStyle.fields?.color)
        : themeColor(
            theme,
            buttonStyle.fields?.textColor
              ? buttonStyle.fields.textColor
              : '#FFF',
          );
    componentStyle.backgroundColor =
      buttonStyle.fields?.variant === 'outlined'
        ? 'transparent'
        : buttonColor(theme, buttonStyle.fields?.color);
  }
  if (style) {
    Object.assign(componentStyle, style);
  }

  const renderEndIcon = () => {
    if (!endIcon) return null;
    if (componentStyle.color && React.isValidElement(endIcon)) {
      const { className, ...restProps } = endIcon.props;
      return React.cloneElement(endIcon, {
        ...restProps,
        style: { ...(endIcon.props.style || {}), color: componentStyle.color },
      });
    }
    return endIcon;
  };

  return (
    // if there is a link, wrap the button in an anchor tag
    link ? (
      <a
        href={link.href}
        target={link.target || '_blank'}
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
      >
        <KitButton
          round
          size={size}
          style={componentStyle}
          data-testid={dataTestId || ''}
        >
          {text}
          {endIcon && <span className="ml-2">{renderEndIcon()}</span>}
        </KitButton>
      </a>
    ) : (
      // otherwise, just render the button
      <KitButton
        round
        size={size}
        style={componentStyle}
        onClick={onClick || (() => {})}
        data-testid={dataTestId || ''}
      >
        {text}
        {endIcon && <span className="ml-2">{renderEndIcon()}</span>}
      </KitButton>
    )
  );
};

export default CommonButton;
